import React, { useContext } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import Section from '@hero/ui-kit/layout/Section';
import P from '@hero/ui-kit/typography/P';
import InlineLoader from '../../../../components/InlineLoader';
import QueryInlineError from '../../../../components/QueryInlineError';
import useDrip from '../../api/useDrip';
import BlockEditor from '../../components/BlockEditor';
import { MarkButton } from '../../components/MarkControls';
import { MarkEmptyState, MarkPageHeader } from '../../components/MarkLayout';
import { UnsavedChangesContext } from '../../components/UnsavedChangesProvider';
import * as layout from '../../components/markLayout.module.css';
import * as styles from './template.module.css';

const DripTemplatePage: React.FC = () => {
    const { dripId, versionNumber, stepId } = useParams<{
        dripId: string;
        versionNumber: string;
        stepId: string;
    }>();
    const location = useLocation();
    const navigate = useNavigate();
    const { anySaving } = useContext(UnsavedChangesContext);
    const numericId = Number(dripId);
    const numericVersion = Number(versionNumber);
    const validId = Number.isSafeInteger(numericId) && numericId > 0;
    const validParams =
        validId && Number.isSafeInteger(numericVersion) && numericVersion > 0 && !!stepId?.trim();
    const latestQuery = useDrip(validParams ? numericId : 0);
    const versionQuery = useDrip(
        validParams ? numericId : 0,
        validParams ? numericVersion : undefined
    );
    const drip = latestQuery.data?.data;
    const snapshot = versionQuery.data?.data;
    const requestedVersion =
        snapshot?.draft_version?.version_number === numericVersion
            ? snapshot.draft_version
            : snapshot?.current_version?.version_number === numericVersion
              ? snapshot.current_version
              : null;
    // Drafts change in place, so their saved step/channel comes from the latest detail.
    const version =
        drip?.draft_version?.version_number === numericVersion
            ? drip.draft_version
            : requestedVersion;
    const step = version?.steps.find((item) => item.id === stepId);
    const hasTemplate =
        step?.type === 'activity' &&
        (step.activity === 'send_email' || step.activity === 'send_sms');
    const channel = step?.type === 'activity' && step.activity === 'send_sms' ? 'sms' : 'email';
    const readOnly =
        drip?.draft_version?.version_number !== numericVersion ||
        (drip?.status !== 'DRAFT' && drip?.status !== 'ACTIVE' && drip?.status !== 'PAUSED');
    const back = () => {
        const search = new URLSearchParams(location.search);
        search.set('tab', 'steps');
        navigate(
            {
                pathname: validId ? `/mark/drips/${numericId}` : '/mark/drips',
                search: search.toString()
            },
            { state: location.state }
        );
    };
    let content: React.ReactNode;
    if (!validParams) {
        content = (
            <MarkEmptyState
                title="Template not found"
                description="The requested drip, version, or step is invalid."
            />
        );
    } else if (latestQuery.isLoading || versionQuery.isLoading) {
        content = <InlineLoader />;
    } else if (latestQuery.error && !drip) {
        content = <QueryInlineError error={latestQuery.error} />;
    } else if (versionQuery.error && !snapshot) {
        content = <QueryInlineError error={versionQuery.error} />;
    } else if (!drip || !version || !step) {
        content = (
            <MarkEmptyState
                title="Template not found"
                description="The requested drip, version, or step is unavailable."
            />
        );
    } else if (!hasTemplate) {
        content = (
            <MarkEmptyState
                title="No template for this step"
                description="Only email and SMS steps have templates."
            />
        );
    } else {
        content = (
            <>
                {readOnly && (
                    <div className={layout.notice}>
                        <P noDefaultMargin size="small">
                            This version is read-only. To edit, open the current draft from Steps.
                        </P>
                    </div>
                )}
                <BlockEditor
                    key={`${numericId}-${numericVersion}-${step.id}-${channel}`}
                    target={{
                        target_type: 'drip_step',
                        drip_id: numericId,
                        step_id: step.id,
                        version_number: numericVersion
                    }}
                    channel={channel}
                    targetKind={drip.target_kind}
                    readOnly={readOnly}
                    workspace={channel === 'email'}
                />
            </>
        );
    }
    return (
        <Section
            subtheme={['regular', 'background']}
            noDefaultPadding
            className={`${layout.page} ${styles.page}`}
        >
            <MarkPageHeader
                title={
                    hasTemplate
                        ? `${readOnly ? 'View' : 'Edit'} drip ${channel === 'sms' ? 'SMS' : 'email'} template`
                        : 'Drip template'
                }
                description={
                    drip && step
                        ? `${drip.name} · Step ${step.id} · ${readOnly ? 'Version' : 'Draft'} v${numericVersion}`
                        : drip?.name
                }
            >
                <MarkButton size="regular" disabled={anySaving} onClick={back}>
                    {validId ? 'Back to drip' : 'Back to drips'}
                </MarkButton>
            </MarkPageHeader>
            {content}
        </Section>
    );
};

export default DripTemplatePage;
